import { useId } from 'react'
import { Button } from 'antd'
import { Check } from 'lucide-react'
import type { AvatarConfig } from '@super-mega/server/contracts/profile'
import { cn } from '@/utils/index.ts'
import {
  DEFAULT_USER_AVATAR,
  USER_AVATAR_BACKGROUND_OPTIONS,
  USER_AVATAR_BACKGROUNDS,
  USER_AVATAR_PATTERNS
} from './avatar-options.ts'
import { UserAvatar } from './user-avatar.tsx'

export type AvatarPickerProps = {
  value?: AvatarConfig | null
  onChange?: (avatar: AvatarConfig | null) => void
  disabled?: boolean
  displayName?: string
  className?: string
}

export function AvatarPicker({
  value,
  onChange,
  disabled = false,
  displayName = '用户',
  className
}: AvatarPickerProps) {
  const generatedId = useId()
  const activePattern = value?.pattern ?? DEFAULT_USER_AVATAR.pattern
  const activeBackground = value?.background ?? DEFAULT_USER_AVATAR.background

  return (
    <fieldset className={cn('m-0 min-w-0 border-0 p-0', className)} disabled={disabled}>
      <legend className="sr-only">设置头像图案与背景色</legend>

      <div className="border-border bg-surface-muted mb-4 flex items-center justify-between gap-4 rounded-xl border p-3">
        <div className="flex min-w-0 items-center gap-3">
          <UserAvatar avatar={value} name={displayName} size={56} />
          <span className="min-w-0">
            <span className="text-content block text-sm font-medium">头像预览</span>
            <span aria-live="polite" className="text-content-soft mt-0.5 block text-xs">
              {value
                ? `${USER_AVATAR_BACKGROUND_OPTIONS[value.background].label} · 图案 ${value.pattern.slice(-2)}`
                : '当前使用姓名首字母'}
            </span>
          </span>
        </div>
        <Button
          disabled={disabled || !value}
          htmlType="button"
          onClick={() => onChange?.(null)}
          size="small"
          type="link"
        >
          恢复默认
        </Button>
      </div>

      <div>
        <div className="text-content-muted mb-2 text-xs font-medium">图案</div>
        <div className="grid grid-cols-6 gap-2 sm:grid-cols-10">
          {USER_AVATAR_PATTERNS.map((pattern, index) => {
            const selected = value?.pattern === pattern
            const optionLabel = `图案 ${String(index + 1).padStart(2, '0')}`

            return (
              <label
                className={cn(
                  'bg-surface relative flex size-12 cursor-pointer items-center justify-center justify-self-center rounded-full border p-1 transition-[border-color,box-shadow,background-color]',
                  'hover:border-accent',
                  'has-[:focus-visible]:ring-accent has-[:focus-visible]:ring-offset-surface has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-offset-2',
                  selected ? 'border-accent ring-accent bg-[var(--accent-soft)] ring-1' : 'border-border-strong',
                  disabled && 'cursor-not-allowed opacity-50'
                )}
                key={pattern}
                title={optionLabel}
              >
                <input
                  aria-label={optionLabel}
                  checked={selected}
                  className="sr-only"
                  name={`avatar-pattern-${generatedId}`}
                  onChange={() => onChange?.({ pattern, background: activeBackground })}
                  type="radio"
                  value={pattern}
                />
                <UserAvatar alt="" avatar={{ pattern, background: activeBackground }} name="" size={40} />
                {selected ? (
                  <span
                    aria-hidden="true"
                    className="border-surface bg-accent text-accent-contrast absolute -right-0.5 -bottom-0.5 flex size-4.5 items-center justify-center rounded-full border-2"
                  >
                    <Check className="size-2.5" strokeWidth={3} />
                  </span>
                ) : null}
              </label>
            )
          })}
        </div>
      </div>

      <div className="mt-4">
        <div className="text-content-muted mb-2 text-xs font-medium">背景色</div>
        <div className="flex flex-wrap gap-2.5 px-2.5">
          {USER_AVATAR_BACKGROUNDS.map((background) => {
            const option = USER_AVATAR_BACKGROUND_OPTIONS[background]
            const selected = value?.background === background

            return (
              <label
                className={cn(
                  'border-surface relative flex size-9 cursor-pointer items-center justify-center rounded-full border-2 shadow-[0_0_0_1px_var(--border-strong)] transition-shadow',
                  'has-[:focus-visible]:ring-accent has-[:focus-visible]:ring-offset-surface has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-offset-2',
                  selected && 'shadow-[0_0_0_2px_var(--accent)]',
                  disabled && 'cursor-not-allowed opacity-50'
                )}
                key={background}
                style={{ backgroundColor: `#${option.color}` }}
                title={option.label}
              >
                <input
                  aria-label={`背景色 ${option.label}`}
                  checked={selected}
                  className="sr-only"
                  name={`avatar-background-${generatedId}`}
                  onChange={() => onChange?.({ pattern: activePattern, background })}
                  type="radio"
                  value={background}
                />
                {selected ? <Check aria-hidden className="size-4 text-slate-700" strokeWidth={2.5} /> : null}
              </label>
            )
          })}
        </div>
      </div>
    </fieldset>
  )
}
